/* ============================================================
   PBK トップ KV 散り石ローテーション <pbk-hero-stones>
   - JS 有効時は焼込み背景を .pbk-hero--live で消し、石タイル
     <a class="pbk-hero__stone"> に画像 2 枚を重ねて描画する。
     初期表示 = Figma の配置そのまま（位置・サイズ・石種）を時差 bloom-in。
   - 一定時間（data-first-delay）経過後、1 個ずつパラパラと石が入れ替わり続ける。

   入替の見え方 = 「消える → 少し空く → 育って現れる」。中心は固定で、回転もクロスフェードもしない。
     スケール・ぼかし・不透明度は **それぞれ独立した時間・遅延・カーブ** で動く（schema で個別に調整可）。
     参照動画（石 1 個の理想モーション・2026-08-01 にユーザー提供）の実測とフィット値:
       - 拡大: 0→100% を 1126ms・cubic-bezier(0.52, 0.38, 0, 1)（終盤ゆっくり）
       - ぼけ(現れる側): 最大 11.2px（石幅 196px の 5.7%）。拡大開始 417ms 後から 709ms かけて
         抜ける・cubic-bezier(0.12, 0, 0.52, 0.84)
       - 消える側: **縮小しない**（scale 1→0.99）。ぼけが 125ms で 10.9px まで乗り
         (cubic-bezier(0.54, 0.3, 0.2, 0.42))、不透明度が 292ms で 0 になる
         (cubic-bezier(0.5, 0.22, 0.56, 1))
     ＝ 消滅は「縮んで消える」ではなく「ぼけながら薄れて消える」。旧実装（不透明度一定 + 縮小のみ・
     参照動画 IBM ICONS 由来）は swap_out_fade_ms を 0 にすれば再現できる。
     **現れる側の不透明度 0→1（swap_in_fade_ms・2026-08-02 追加）は参照動画に無い足し込み**:
     動画の現れる側は opacity 一定だが、消える側がほぼ縮まない設定だとスケールだけでは
     入替が見えにくい（＝「縮小の時間を変えても見た目が変わらない」の正体）。
     出入りの両方で濃さが動くようにした。0 にすれば動画そのままの挙動に戻る。
     **既定値の正本 = sections/custom-top-hero.liquid の schema "default"（唯一の変更箇所）**。
     section が全パラメータを data 属性で必ず出力するため、この JS はフォールバック値を
     持たない（各値の意図は schema の info・確定の経緯は git log 参照）。
     旧 IBM ICONS 由来の値（240-200-240 + フィットしたカーブ）も schema・パネルの選択肢として残してある。

   入替の選び方 = 「2 スロットの石を交換する」(2026-07-29 仕様):
     (1) 「ピース」= Figma に置かれている石 1 個（画像 + その石のサイズ）。データ行と 1:1。
         サイズはピースに固定で、どのスロットへ移ってもそのサイズで描画する（枠に合わせて
         拡縮しない）。同じ画像でも別サイズなら別ピース（例 PC の f21 系は 3 枚で 138.5/105.1/84.4）
         → 画像単位でまとめるとサイズ配分が潰れるため。スロットは「中心座標」だけを持つ。
     (2) 交換方式なので画面上の石の集合＝常に Figma の集合（種類も個数もサイズ配分も不変）。
         全種類が必ず出続け、特定の石が消えたり増えたりしない = 「良い感じにバラバラ」。
         単スロット置換だと SP(14 枠 = 14 個)では必ず種類が欠けるため採らない。
     (3) 隣同士が同じ石にならない: 各スロットの近傍 K 個に同じ石種を置かない（判定は data-stone）。
         判定は 2026-07-29 に src → 石種へ広げた。青いエメラルドカットのトパーズ f60 / f06 の
         ように「別画像だが同じ石にしか見えない」組があり、src 判定では隣り合えてしまうため
         （f60 / f06 のホームスロットは相互最近傍）。副作用として Figma の初期配置にある
         f04(淡いアメシスト) と f16(濃いアメシスト) の隣接は入替後は再現しなくなる
         ── 初期表示は Figma のままで、制約は入替時にだけ効く。
     (4) 遮蔽を増やさない: 交換後の重なり率が「初期配置(Figma)の最大重なり率」を超える
         組合せは選ばない（素材の多くは透過だが正式写真は #f5f3ee 焼込みで不透明）。
     (5) 端の見切れは初期配置(Figma)の最大はみ出し率が上限（_initLayer が導出。2026-08-02 に
         定数 30%/45% から変更 ── 配置クランプ撤去で初期はみ出しが定数超の石が出たため）。
     (6) 交換した 2 個は同時に動かさず、2 個目を data-pair-offset だけ遅らせる（同時だと
         機械的に見えるため）。ずらしている間も石の集合・近傍条件は崩れない（片方が移動しただけ）。
     (7) 同じスロットが立て続けに変わらないよう、スロット単位のクールダウン(data-interval)。
     旧ロジック（解像度ガード #159 / 縦横比の近さ優先 #104 / 表示中 src 全除外）は撤去:
     ピース固有サイズ描画では拡大が起きない（＝粗くならない）し余白も出ないため役目を終えた。
     PC は 27 枠に対しユニーク画像が少なく「表示中 src 全除外」だと候補ゼロ＝入替不能だった
     不具合も解消。
   - 停止条件: 石 hover 中 / タブ非表示 / KV 画面外 / data-static。
     停止時はタイル撤去 + 焼込み背景に復帰（ゲート撮影の決定論化にも使用）。
     prefers-reduced-motion では止めない（OS 設定 ON でも KV 演出は動かす指定）。
   - アニメの時間・スケール・stagger・間隔は section schema（data 属性）で調整可能。
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('pbk-hero-stones')) return;

  /* schema に出している調整値の既定はこの JS には持たない ── 正本 = custom-top-hero.liquid の
     schema "default"（section が全 data 属性を必ず出力する。1 箇所変更で全反映）。
     ここに残る定数は schema に出していない固定値だけ。 */
  /* 交換相手に許す面積差（%）。ピースは自分のサイズを保って動くため、L の場所に S が入ると
     設計のサイズ配分が崩れる（CL 指摘 Issue #273「L のところは L、M は M、S は S に」）。
     面積比が 1±この値に収まる相手だけと交換する = サイズ階級が保たれる。 */
  var SIZE_TOL_PCT = 20;
  /* フェードのカーブは参照実測のフィット値で固定（調整項目を増やしすぎないため） */
  var OUT_FADE_EASE = 'cubic-bezier(0.5, 0.22, 0.56, 1)';
  var IN_FADE_EASE = 'cubic-bezier(0, 0, 0.2, 1)'; /* だんだん遅く（濃くなりきる手前で緩む） */
  /* 見切れ上限のフォールバック（実際は _initLayer が初期配置=Figma から導出した
     layer._bleedX/_bleedY を使う。クランプ撤去 2026-08-02 以降、配置は Figma 素の座標） */
  var BLEED_X = 0.3;
  var BLEED_Y = 0.45;

  /* detached img の decode() は永遠に未解決になり得る → タイムアウトで必ず進める */
  function decodeWithTimeout(img, ms) {
    var decoded = img.decode ? img.decode().catch(function () {}) : Promise.resolve();
    var timeout = new Promise(function (r) { setTimeout(r, ms); });
    return Promise.race([decoded, timeout]);
  }

  function numOr(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }

  /* キャンバス比（0..1）空間の矩形。石はスロット中心に対して中央配置 */
  function rectOf(geom, mat) {
    return { x: geom.cxR - mat.wR / 2, y: geom.cyR - mat.hR / 2, w: mat.wR, h: mat.hR };
  }

  /* 重なり面積 ÷ 小さい方の面積（x/y の単位が違っても比なので不変） */
  function overlapRatio(a, b) {
    var ox = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
    var oy = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
    if (ox <= 0 || oy <= 0) return 0;
    return (ox * oy) / Math.min(a.w * a.h, b.w * b.h);
  }

  /* 端の見切れが上限内か（上端は制限しない = ヘッダー裏で Figma と同じ見え方）。
     上限 = 初期配置(Figma)の最大はみ出し率（layer._bleedX/_bleedY・_initLayer で導出）。
     EPS: Liquid が style に出す % は丸められる（例 width:34.8533%）ため、上限
     ちょうどに置かれた石は境界判定が 1e-7 ほどぶれる → 0.01% ≒ 0.15px の許容を持たせる */
  var EPS = 1e-4;
  function bleedOk(geom, mat, layer) {
    var bxr = layer && layer._bleedX !== undefined ? layer._bleedX : BLEED_X;
    var byr = layer && layer._bleedY !== undefined ? layer._bleedY : BLEED_Y;
    var r = rectOf(geom, mat);
    return r.x >= -mat.wR * bxr - EPS
      && r.x + r.w <= 1 + mat.wR * bxr + EPS
      && r.y + r.h <= 1 + mat.hR * byr + EPS;
  }

  class PbkHeroStones extends HTMLElement {
    /* data-static の付け外しで停止/再開できる（ゲート撮影時の決定論化にも使用:
       撮影前に setAttribute('data-static','') → タイル即撤去 = 焼込み背景の初期状態に戻る） */
    static get observedAttributes() { return ['data-static']; }

    attributeChangedCallback() {
      if (this._mqSp) this._restart();
    }

    connectedCallback() {
      /* アニメーションのパラメータはすべて schema 設定（data 属性）から。
         section が全属性を必ず出力する（正本 = schema "default"）ため、ここのフォールバック
         0/'' は「属性の書き忘れ・改名事故」を目立たせるための最小値で、既定値ではない */
      this._interval = numOr(this.dataset.interval, 0);   /* 同一スロットのクールダウン */
      this._outMs = numOr(this.dataset.swapOut, 0);       /* 縮小の時間 */
      this._inMs = numOr(this.dataset.swapIn, 0);         /* 拡大の時間 */
      this._swapGap = numOr(this.dataset.swapGap, 0);     /* 消えている時間 */
      this._outEase = this.dataset.outEase || '';
      this._inEase = this.dataset.inEase || '';
      /* ぼかし・フェードの時間はスケールとは別に持つ（Figma/参照動画のタイミングが別だから） */
      this._inBlurMs = numOr(this.dataset.inBlurMs, 0);
      this._inBlurDelay = numOr(this.dataset.inBlurDelay, 0);
      this._inBlurEase = this.dataset.inBlurEase || '';
      this._outBlurMs = numOr(this.dataset.outBlurMs, 0);
      this._outBlurEase = this.dataset.outBlurEase || '';
      this._outFadeMs = numOr(this.dataset.outFadeMs, 0);
      this._inFadeMs = numOr(this.dataset.inFadeMs, 0);
      this._minScale = numOr(this.dataset.minScale, 0) / 100; /* 消える側の到達サイズ */
      this._inFrom = numOr(this.dataset.inFrom, 0) / 100;     /* 現れる側の開始サイズ */
      this._everyMs = numOr(this.dataset.every, 0);           /* 入替を始める平均間隔 */
      this._jitter = numOr(this.dataset.jitter, 0) / 100;     /* 間隔のばらつき */
      this._pairOffset = numOr(this.dataset.pairOffset, 0);   /* 2 個目のずらし幅 */
      this._enterStagger = numOr(this.dataset.enterStagger, 0);
      this._enterScaleFrom = 1 + numOr(this.dataset.enterScale, 0) / 100; /* 初回出現の開始スケール（例 1.06） */
      this._activeRatio = numOr(this.dataset.activeRatio, 0) / 100; /* 常時入替中の割合 */
      this._sizeTol = numOr(this.dataset.sizeTol, SIZE_TOL_PCT) / 100; /* 交換相手に許す面積差（schema 非公開） */
      this._enterDelay = numOr(this.dataset.enterDelay, 0);   /* ルース出現の開始遅延 */
      this._firstDelay = numOr(this.dataset.firstDelay, 0);   /* 出現後、入替開始までの待ち */

      this._timer = null;
      this._hovering = false;
      this._inView = true;
      this._layer = null;

      this._mqSp = window.matchMedia('(max-width: 749px)');
      this._onModeChange = this._restart.bind(this);
      this._mqSp.addEventListener('change', this._onModeChange);

      /* 石 hover 中はクリック先が変わらないよう入替を保留 */
      this.addEventListener('pointerover', (e) => {
        if (e.target.closest && e.target.closest('.pbk-hero__stone')) this._hovering = true;
      });
      this.addEventListener('pointerout', (e) => {
        if (e.target.closest && e.target.closest('.pbk-hero__stone')) this._hovering = false;
      });

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => { this._inView = entry.isIntersecting; });
        });
        /* 自身は display:contents でボックスを持たない → 監視対象は親の .pbk-hero */
        this._io.observe(this.closest('.pbk-hero') || this);
      }

      /* 幅可変（aspect-ratio）なのでキャンバス実寸が変わったら石の px サイズを引き直す */
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => { this._resize(); });
      }

      this._restart();
    }

    disconnectedCallback() {
      this._stop();
      this._mqSp.removeEventListener('change', this._onModeChange);
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
    }

    _stop() {
      clearTimeout(this._timer);
      this._timer = null;
    }

    _hero() {
      return this.closest('.pbk-hero');
    }

    /* タイルを撤去して焼込み背景だけの初期状態へ戻す */
    _resetLayers() {
      var hero = this._hero();
      if (hero) hero.classList.remove('pbk-hero--live');
      if (this._ro) this._ro.disconnect();
      this.querySelectorAll('.pbk-hero__stones').forEach(function (layer) {
        (layer._slots || []).forEach(function (a, i) {
          if (a._front) a._front.remove();
          if (a._back) a._back.remove();
          delete a._front;
          delete a._back;
          delete a._busy;
          /* Figma 由来の初期ボックスへ戻す（交換で書き換えた left/top/width/height を復旧） */
          if (layer._geom && layer._geom[i]) {
            var g = layer._geom[i];
            a.style.left = (g.x0 * 100) + '%';
            a.style.top = (g.y0 * 100) + '%';
            a.style.width = (g.wR0 * 100) + '%';
            a.style.height = (g.hR0 * 100) + '%';
          }
        });
        delete layer._slots;
        delete layer._geom;
        delete layer._pieces;
        delete layer._near;
        delete layer._cur;
        delete layer._last;
        delete layer._overlapCap;
        delete layer._entered;
      });
    }

    _restart() {
      this._stop();
      if (this.hasAttribute('data-static')) {
        this._resetLayers();
        return;
      }
      var layer = this.querySelector(this._mqSp.matches ? '.pbk-hero__stones--sp' : '.pbk-hero__stones--pc');
      if (!layer) return;
      this._layer = layer;
      this._initLayer(layer);
      if (this._ro) this._ro.observe(layer);
      if (layer._entered) {
        var hero = this._hero();
        if (hero) hero.classList.add('pbk-hero--live');
        this._schedule(0);
      } else {
        this._enter(layer);
      }
    }

    /* レイヤー初期化: スロット幾何・素材表・近傍・重なり上限を Figma 由来の DOM から導出。
       サイズの正本は Liquid のデータ行（= Figma 実測）なので JS は静的表を持たない。 */
    _initLayer(layer) {
      if (layer._pieces) return;
      var slots = Array.prototype.slice.call(layer.querySelectorAll('.pbk-hero__stone'));
      layer._slots = slots;

      /* style の % から比を読む。ここで読んだ値だけが正本（以後 style は交換で書き換わる） */
      layer._geom = slots.map(function (a) {
        var wR0 = parseFloat(a.style.width) / 100;
        var hR0 = parseFloat(a.style.height) / 100;
        var x0 = parseFloat(a.style.left) / 100;
        var y0 = parseFloat(a.style.top) / 100;
        return { x0: x0, y0: y0, wR0: wR0, hR0: hR0, cxR: x0 + wR0 / 2, cyR: y0 + hR0 / 2 };
      });

      /* 「ピース」= Figma に置かれている石そのもの（画像 + その石のサイズ）。スロットと 1:1。
         同じ画像が別サイズで複数置かれている（例 PC の f21 系は 138.5 / 105.1 / 84.4 の 3 枚）ため、
         画像単位でまとめるとサイズが 1 つに潰れて設計のサイズ配分が崩れる → ピース単位で持つ。
         交換で動かしてもピースは自分のサイズを保つ = 「石ごとに決まった初期のサイズのまま」。
         PC 専用の石を SP に混ぜないのは、SP の固有サイズが Figma に無く推測になるため。 */
      layer._pieces = slots.map(function (a, i) {
        return {
          src: a.dataset.img,
          href: a.getAttribute('href'),
          label: a.getAttribute('aria-label'),
          stone: a.dataset.stone,
          wR: layer._geom[i].wR0,
          hR: layer._geom[i].hR0
        };
      });
      layer._cur = slots.map(function (a, i) { return i; });
      layer._last = slots.map(function () { return 0; });
      /* スロットが「もともと持っていた石の面積」。交換の可否は毎回この初期値と比べる
         （直前の石と比べると入替のたびに ±tol ずつドリフトして L が M になる・Issue #273） */
      layer._homeArea = slots.map(function (a, i) { return layer._pieces[i].wR * layer._pieces[i].hR; });

      /* 近傍 = 中心距離の近い K 個。距離はキャンバスのアスペクトで y を補正して等方に測る。
         K 近傍は非対称（j が i の近傍でも i が j の近傍とは限らない・PC で 19 本）ので
         対称化する。非対称のままだと「相手側から見て隣に同じ石」が通り抜ける（実測 27 件）。
         対称化しても初期配置(Figma)の近傍同一は 0 なので制約として成立する。 */
      var rect = layer.getBoundingClientRect();
      var aspect = rect.width ? rect.height / rect.width : 1;
      var geom = layer._geom;
      var K = slots.length >= 20 ? 5 : 4;
      layer._near = geom.map(function (g, i) {
        return geom
          .map(function (t, j) {
            return { j: j, d: Math.hypot(g.cxR - t.cxR, (g.cyR - t.cyR) * aspect) };
          })
          .filter(function (o) { return o.j !== i; })
          .sort(function (p, q) { return p.d - q.d; })
          .slice(0, K)
          .map(function (o) { return o.j; });
      });
      layer._near.forEach(function (list, i) {
        list.slice().forEach(function (j) {
          if (layer._near[j].indexOf(i) < 0) layer._near[j].push(i);
        });
      });

      /* 遮蔽の許容量 = 初期配置(Figma)の最大重なり率（PC 9.1% / SP 21.2% 実測）。
         デザイナー自身の最悪ケースを上限にする＝新しい遮蔽を作らない */
      var cap = 0;
      for (var i = 0; i < slots.length; i++) {
        for (var j = i + 1; j < slots.length; j++) {
          cap = Math.max(cap, overlapRatio(
            rectOf(geom[i], layer._pieces[layer._cur[i]]),
            rectOf(geom[j], layer._pieces[layer._cur[j]])
          ));
        }
      }
      layer._overlapCap = cap;

      /* 見切れの許容量も初期配置(Figma)から導出（遮蔽と同じ思想）。
         クランプ撤去（2026-08-02・全石 Figma どおり）で初期はみ出しが旧上限 30%/45% を
         超える石がある（例 PC f14 は左 80%）ため、定数だと初期配置自体が違反になり
         その石が入替不能になる。入替は「Figma の最悪ケースより悪い見切れを作らない」 */
      var bx = 0, by = 0;
      for (var i2 = 0; i2 < slots.length; i2++) {
        var r0 = rectOf(geom[i2], layer._pieces[layer._cur[i2]]);
        bx = Math.max(bx, -r0.x / r0.w, (r0.x + r0.w - 1) / r0.w);
        by = Math.max(by, (r0.y + r0.h - 1) / r0.h);
      }
      layer._bleedX = Math.max(bx, 0);
      layer._bleedY = Math.max(by, 0);

      slots.forEach(function (a) {
        var front = document.createElement('img');
        var back = document.createElement('img');
        [front, back].forEach(function (img) {
          img.className = 'pbk-hero__stone-img';
          img.alt = '';
          img.decoding = 'async';
          img.draggable = false;
          a.appendChild(img);
        });
        a._front = front;
        a._back = back;
      });

      this._measure(layer);
      slots.forEach((a, i) => { this._sizeImg(a._front, layer._pieces[layer._cur[i]]); });
    }

    /* キャンバス実寸を測る（石の px サイズの基準） */
    _measure(layer) {
      var rect = layer.getBoundingClientRect();
      layer._pxW = rect.width;
      layer._pxH = rect.height;
    }

    /* 石は「自分の固有サイズ」で描画する。スロット枠に依存させないため px で指定し、
       スロット中心に対して margin で中央合わせする（transform は scale 演出専用に空けておく） */
    _sizeImg(img, mat) {
      if (!img || !mat) return;
      var layer = img.closest('.pbk-hero__stones');
      if (!layer || !layer._pxW) return;
      img.style.setProperty('--pbk-iw', (mat.wR * layer._pxW) + 'px');
      img.style.setProperty('--pbk-ih', (mat.hR * layer._pxH) + 'px');
    }

    /* ぼかし量(px)。正本は .pbk-hero の --pbk-blur（schema blur_px / 調整パネルが書き換える）。
       0 のときは filter に一切触らない ── blur(0px) でも合成レイヤーが増えるため、
       既定（CL 指定のブラー無し・#104）では従来どおり filter を持たせない。
       毎回 computed から読むので、調整パネルで値を変えれば次の入替から効く。 */
    _blurPx() {
      var hero = this._hero();
      if (!hero) return 0;
      var n = parseFloat(getComputedStyle(hero).getPropertyValue('--pbk-blur'));
      return isFinite(n) && n > 0 ? n : 0;
    }

    /* リンク/hover 領域を表示中の石に合わせる（中心は不変なので見た目は動かない） */
    _fitBox(a, geom, mat) {
      a.style.left = ((geom.cxR - mat.wR / 2) * 100) + '%';
      a.style.top = ((geom.cyR - mat.hR / 2) * 100) + '%';
      a.style.width = (mat.wR * 100) + '%';
      a.style.height = (mat.hR * 100) + '%';
    }

    _resize() {
      var layer = this._layer;
      if (!layer || !layer._slots) return;
      this._measure(layer);
      var self = this;
      layer._slots.forEach(function (a, i) {
        var mat = layer._pieces[layer._cur[i]];
        self._sizeImg(a._front, mat);
        if (a._back && a._back.src) self._sizeImg(a._back, mat);
      });
    }

    /* 初期出現: 焼込み背景を消し、Figma のままの配置を時差 bloom-in → 一定時間後に入替開始 */
    _enter(layer) {
      var self = this;
      var hero = this._hero();
      /* 先に背景を消しておく（beige の上に石が咲く。画像未ロードでも破綻しない） */
      if (hero) hero.classList.add('pbk-hero--live');

      var loads = layer._slots.map(function (a) {
        a._front.src = a.dataset.img;
        return decodeWithTimeout(a._front, 2000);
      });

      Promise.all(loads).then(function () {
        if (!layer._slots) return; /* enter 中に reset された */
        /* タイトルが下から出た後に、少し遅らせてルースをふわっと咲かせる */
        setTimeout(function () {
          if (!layer._slots) return; /* 遅延中に reset された */
          layer._slots.forEach(function (a) {
            if (!a._front) return;
            a.style.setProperty('--pbk-stagger', Math.round(Math.random() * self._enterStagger) + 'ms');
            var img = a._front;
            img.style.transition = 'none';
            img.style.transform = 'scale(' + self._enterScaleFrom + ')';
            void img.offsetWidth;
            img.style.transition = '';
            img.style.transform = '';
            img.classList.add('is-shown');
          });
          layer._entered = true;
          /* 「一定時間はデザインどおり」→ 出現後 _firstDelay 待ってから入替を始める */
          self._schedule(self._firstDelay);
        }, self._enterDelay);
      });
    }

    /* 連続ドリップ scheduler: 常に約 _activeRatio の石が遷移中になるよう、
       一定リズム(±jitter)で「交換 1 組」を開始する。全一斉バーストにはしない。 */
    _schedule(startDelay) {
      var self = this;
      var layer = this._layer;
      if (!layer || !layer._slots) return;
      clearTimeout(this._timer);

      var n = layer._slots.length;
      var activeTarget = Math.max(2, Math.round(n * this._activeRatio));
      /* 間隔は設定値（data-every）優先。0 なら「同時に入替中の割合」から自動算出する:
         1 サイクル = 縮小 + 空白 + 拡大。1 回の交換で 2 スロット動くので 2 スロット分で割る */
      var cycle = this._outVisibleMs() + this._swapGap + this._inDur();
      var tickAvg = this._everyMs > 0
        ? Math.max(60, Math.round(this._everyMs))
        : Math.max(120, Math.round(cycle * 2 / activeTarget));
      var jit = this._jitter;

      var tick = function () {
        if (!self._hovering && !document.hidden && self._inView) {
          var slots = layer._slots || [];
          var busy = 0;
          for (var i = 0; i < slots.length; i++) if (slots[i]._busy) busy++;
          if (busy + 2 <= activeTarget) self._exchangeOne(layer);
        }
        var jitter = 1 - jit + Math.random() * jit * 2; /* 平均 1 で ±jit */
        self._timer = setTimeout(tick, Math.round(tickAvg * jitter));
      };

      var first = startDelay > 0 ? startDelay : Math.round(tickAvg * (1 - jit + Math.random() * jit * 2));
      this._timer = setTimeout(tick, first);
    }

    /* スロット i にピース m を置いて良いか（近傍に同じ石が無い・遮蔽が上限以下・見切れ上限内）。
       同一判定は石種 (data-stone) で行う ── 画像 (src) 単位だと、同じ石種で見た目がほぼ
       同じ別画像が隣り合う（例 PC の f60 と f06 はどちらも青いエメラルドカットのトパーズで、
       そのホームスロット 3 と 6 は相互最近傍。src 判定では素通りしていた = CL 指摘）。
       石種は src より粗い（同じ src は必ず同じ石種）ので、旧 src 判定を包含する。
       ignore = 判定から除くスロット（交換相手は同時に入れ替わるため） */
    _placeable(layer, i, m, ignore) {
      var mat = layer._pieces[m];
      if (!bleedOk(layer._geom[i], mat, layer)) return false;
      var box = rectOf(layer._geom[i], mat);
      var near = layer._near[i];
      for (var k = 0; k < near.length; k++) {
        var j = near[k];
        if (j === ignore) continue;
        if (layer._pieces[layer._cur[j]].stone === mat.stone) return false;
        if (overlapRatio(box, rectOf(layer._geom[j], layer._pieces[layer._cur[j]])) > layer._overlapCap + 1e-9) return false;
      }
      return true;
    }

    /* ピース m をスロット i に置いてもサイズ階級が変わらないか（初期面積比が 1±_sizeTol） */
    _sizeOk(layer, i, m) {
      var home = layer._homeArea[i];
      if (!home) return true;
      var r = (layer._pieces[m].wR * layer._pieces[m].hR) / home;
      return r >= 1 - this._sizeTol && r <= 1 + this._sizeTol;
    }

    /* 交換 1 組を選んで実行。相手は「近傍でない」スロットに限る
       （近傍同士の交換は動きが分かりにくく、近傍同一の判定も自明に崩れやすい） */
    _exchangeOne(layer) {
      var now = Date.now();
      var slots = layer._slots;
      var ready = [];
      for (var i = 0; i < slots.length; i++) {
        if (!slots[i]._busy && now - layer._last[i] >= this._interval) ready.push(i);
      }
      if (ready.length < 2) return;

      var a = ready[Math.floor(Math.random() * ready.length)];
      var mi = layer._cur[a];
      var cands = [];
      for (var n = 0; n < ready.length; n++) {
        var b = ready[n];
        if (b === a) continue;
        var mj = layer._cur[b];
        if (layer._pieces[mj].src === layer._pieces[mi].src) continue; /* 同じ石の入替は見た目が変わらない */
        if (layer._near[a].indexOf(b) >= 0 || layer._near[b].indexOf(a) >= 0) continue;
        /* サイズ階級を保つ: 交換後の石が「そのスロットの初期サイズ」±_sizeTol に収まる組だけ許す
           （L の場所には L、M には M、S には S・Issue #273） */
        if (!this._sizeOk(layer, a, mj) || !this._sizeOk(layer, b, mi)) continue;
        /* 交換後の状態で両側を検査（互いは無視 = 同時に入れ替わるため） */
        layer._cur[a] = mj;
        layer._cur[b] = mi;
        var ok = this._placeable(layer, a, mj, b) && this._placeable(layer, b, mi, a);
        layer._cur[a] = mi;
        layer._cur[b] = mj;
        if (ok) cands.push(b);
      }
      if (!cands.length) return; /* 条件を満たす相手が無い → 次 tick で別スロットから試す */

      var partner = cands[Math.floor(Math.random() * cands.length)];
      this._applyExchange(layer, a, partner);
    }

    /* 交換した 2 個を「同時に」動かすと機械的に見えるので、時間をずらして 1 個ずつ動かす。
       ただし単純に 2 個目を遅らせるだけだと、i に j の石が育った時点で j にはまだ同じ石が
       残っており「同じ石が 2 箇所に見える」区間(実測 1.6 秒)ができる。そこで順序を
         ① i が縮んで消える → ② 少し遅れて j も消える → ③ i に j の石が育つ →
         ④ 少し遅れて j に i の石が育つ
       にする。どの瞬間でも同じ石が 2 つ見えない（消えている間に入れ替わるため）。 */
    _applyExchange(layer, i, j) {
      var self = this;
      var mi = layer._cur[i];
      var mj = layer._cur[j];
      layer._cur[i] = mj;
      layer._cur[j] = mi;
      layer._last[i] = layer._last[j] = Date.now();

      var A = layer._slots[i];
      var B = layer._slots[j];
      if (!A || !B || !A._back || !B._back) return;
      A._busy = true;
      B._busy = true;

      var off = Math.round(this._pairOffset * (0.6 + Math.random() * 0.8)); /* ±40% で不規則に */
      var pairIn = Math.round(off * 0.5); /* 育つ側もずらす */
      var tOutB = off;
      /* 空白は石が「見えなくなった瞬間」から数える（見えない縮小の残りは待たない） */
      var tInA = off + this._outVisibleMs() + this._swapGap;
      var tInB = tInA + pairIn;

      /* 画像は先に読み込んでおく（表示タイミングをデコード待ちで崩さない） */
      this._preload(A, layer._pieces[mj]);
      this._preload(B, layer._pieces[mi]);

      this._hide(A);
      setTimeout(function () { if (layer._slots) self._hide(B); }, tOutB);
      setTimeout(function () { if (layer._slots) self._show(layer, i, mj); }, tInA);
      setTimeout(function () { if (layer._slots) self._show(layer, j, mi); }, tInB);
    }

    /* 石が「見えなくなる」までの時間 = ② 消えている時間（_swapGap）の起点。
       フェードありなら opacity 0 の時点（縮小がまだ動いていても見えないので待たない —
       以前は縮小・ぼけ・フェードの最大値を待っていて、縮小 800ms × フェード 290ms だと
       差の 510ms が「見えない待ち」= 設定した空白より体感の余白がずっと長くなっていた）。
       フェード無し(0)は縮み切る時点。残った縮小・ぼけの transition は別 img 要素なので
       次の石と並走しても衝突しない */
    _outVisibleMs() {
      if (this._outFadeMs > 0) {
        return this._minScale <= 0 ? Math.min(this._outFadeMs, this._outMs) : this._outFadeMs;
      }
      return this._outMs;
    }

    /* 現れる側が終わるまでの実時間（拡大・遅れて抜けるぼけ・フェードインのうち最も遅いもの） */
    _inDur() {
      var blur = this._blurPx();
      return Math.max(this._inMs, blur ? this._inBlurDelay + this._inBlurMs : 0, this._inFadeMs);
    }

    /* 次に出す石を back img に用意しておく（開始スケール + ぼかし + 透明で待機 = 見えない）。
       .is-shown の filter:blur(0) / opacity:1 より inline が強いので、ここが入替演出の起点になる。
       opacity は前回の消滅でフェードした値が残っているため必ず明示する
       （フェードイン無し = '' に戻して .is-shown の 1 を使う） */
    _preload(a, mat) {
      if (!a._back || !mat) return;
      var blur = this._blurPx();
      a._back.src = mat.src;
      this._sizeImg(a._back, mat);
      a._back.style.transition = 'none';
      a._back.style.transform = 'scale(' + this._inFrom + ')';
      a._back.style.filter = blur ? 'blur(' + blur + 'px)' : '';
      a._back.style.opacity = this._inFadeMs > 0 ? '0' : '';
      a._back.classList.add('is-shown');
      void a._back.offsetWidth;
      decodeWithTimeout(a._back, 1500);
    }

    /* 今の石が消える。スケール・ぼかし・不透明度をそれぞれ独自の時間とカーブで動かす
       （参照動画では縮小はほぼ無く「ぼけながら薄れて消える」= fade が主役） */
    _hide(a) {
      if (!a || !a._front) return;
      var blur = this._blurPx();
      var parts = ['transform ' + this._outMs + 'ms ' + this._outEase];
      if (blur) parts.push('filter ' + this._outBlurMs + 'ms ' + this._outBlurEase);
      if (this._outFadeMs > 0) parts.push('opacity ' + this._outFadeMs + 'ms ' + OUT_FADE_EASE);
      a._front.style.transition = parts.join(', ');
      a._front.style.transform = 'scale(' + this._minScale + ')';
      /* blur 0 のときは inline を空に戻す（値を下げた直後にぼけが残らないよう） */
      a._front.style.filter = blur ? 'blur(' + blur + 'px)' : '';
      if (this._outFadeMs > 0) a._front.style.opacity = '0';
    }

    /* 用意しておいた石が中心から育つ。リンク先・当たり判定は「消えている間」に差し替える
       （見えているタイミングでクリック先がすり替わらない） */
    _show(layer, i, m) {
      var self = this;
      var a = layer._slots[i];
      var mat = layer._pieces[m];
      if (!a || !a._front || !a._back || !mat) return;

      var incoming = a._back;
      var outgoing = a._front;
      a.href = mat.href;
      a.setAttribute('aria-label', mat.label);
      if (mat.stone) a.dataset.stone = mat.stone;
      if (layer._geom && layer._geom[i]) this._fitBox(a, layer._geom[i], mat);
      outgoing.classList.remove('is-shown');

      /* 待機中に blur を持たせた石だけ、育ってから遅れてピントが合う（preload 時の値に追従）。
         ぼけは拡大とは別の時間・遅延・カーブで動く（参照動画では拡大の途中から抜け始める） */
      var hasBlur = !!incoming.style.filter;
      var parts = ['transform ' + this._inMs + 'ms ' + this._inEase];
      if (hasBlur) parts.push('filter ' + this._inBlurMs + 'ms ' + this._inBlurEase + ' ' + this._inBlurDelay + 'ms');
      if (this._inFadeMs > 0) parts.push('opacity ' + this._inFadeMs + 'ms ' + IN_FADE_EASE);
      incoming.style.transition = parts.join(', ');
      incoming.style.transform = 'scale(1)';
      if (hasBlur) incoming.style.filter = 'blur(0px)';
      if (this._inFadeMs > 0) incoming.style.opacity = '1';
      a._front = incoming;
      a._back = outgoing;
      setTimeout(function () { a._busy = false; }, self._inDur() + 60);
    }
  }

  customElements.define('pbk-hero-stones', PbkHeroStones);
})();
